// SB6-ANA-PROG-03 — the consolidated analysis status overlay.
//
// This is the SINGLE progress surface for the analysis workspace, evolved
// from the PROG-01 bottom statusbar + the (now-deleted) PROG-01 readiness
// banner. It is a `position: fixed; bottom: 0` overlay across the whole
// page: content scrolls BEHIND it, it never participates in layout, so it
// causes ZERO layout shift when it appears/disappears (AC1) and it stays
// pinned to the viewport bottom while the workspace scrolls (AC2).
//
// Chrome (workspace-chrome rule, memory `feedback_workspace_canvas_color`):
// canvas-coloured (`background.default`), a subtle top hairline + a faint
// elevation shadow so it reads as an overlay — NO glass.
//
// AUTO-HIDES when idle. It is visible iff ANY of:
//   1. dataset-open readiness has not reached `dataPointReady` — it shows the
//      step sequence "Loading recording ✓ · Applying filter ✓ · Preparing
//      data point …" with each step checking off (the old banner's content),
//      OR the named failed step + a Retry affordance on error (AC4 — relocated
//      from the banner, PROG-01 AC4 semantics preserved);
//   2. a filter change is applying (`useFilterApplyState().applying` — any
//      dependent data-points query out-of-sync) → "Applying filter …";
//   3. an explicit dataset download is running (`isDownloading`) → the
//      "⬇ 124/369 images cached" readout + a Cancel passthrough.
//
// Window-prefetch activity alone does NOT show the overlay (it does not set
// `isDownloading`), so navigation never flickers the bar.
//
// `aria-live="polite"` so a screen reader hears the progression / apply /
// download state without it stealing focus.
import { useEffect } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  useAnalysisReadinessStore,
  type ReadinessStep,
} from "../state/analysisReadinessStore.js";
import { usePrefetchProgress } from "../cache/usePrefetch.js";
import { usePrefetchControls } from "../cache/PrefetchProvider.js";
import { prefetchScheduler } from "../cache/prefetchQueue.js";
import { useFilterApplyState } from "../filterRevision.js";
import {
  CheckIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  DownloadIcon,
  XIcon,
} from "@aeriosiq/ui/icons";

/** How long the terminal "Downloaded …" state lingers before auto-hiding. */
const TERMINAL_LINGER_MS = 5000;

const STEP_LABELS: Record<ReadinessStep, string> = {
  detail: "recording",
  prefs: "preferences",
  images: "images",
  findings: "AI findings",
  defects: "defects",
  dataPoints: "data points",
};

type StepStatus = "done" | "active" | "pending";

function StepChip({
  label,
  status,
}: {
  label: string;
  status: StepStatus;
}): React.ReactElement {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        // ANA-R2-07 a11y — the pending step label previously used
        // `text.disabled` (#a1a1a6), which measures 2.36:1 on the canvas
        // (#f5f5f7) and fails WCAG 2.2 AA. Use `text.secondary` (#6e6e73,
        // ~5.7:1) for ALL states; the pending vs done/active distinction is
        // already carried by the icon (empty ring / spinner / check), not by
        // a sub-AA text colour.
        color: "text.secondary",
      }}
    >
      {status === "done" ? (
        <CircleCheckIcon
          sx={{ fontSize: 16, color: "success.main" }}
          aria-hidden
        />
      ) : status === "active" ? (
        <CircularProgress size={12} thickness={5} aria-hidden />
      ) : (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            border: (t) => `1.5px solid ${t.palette.divider}`,
          }}
          aria-hidden
        />
      )}
      <Typography variant="caption" sx={{ fontWeight: 500 }}>
        {label}
        {status === "active" ? " …" : ""}
      </Typography>
    </Stack>
  );
}

/** The readiness step sequence (3 user-facing steps), as the old banner. */
function ReadinessSteps(): React.ReactElement {
  const coreDataReady = useAnalysisReadinessStore((s) => s.coreDataReady);
  const filterApplied = useAnalysisReadinessStore((s) => s.filterApplied);
  const dataPointReady = useAnalysisReadinessStore((s) => s.dataPointReady);

  const recordingStatus: StepStatus = coreDataReady ? "done" : "active";
  const filterStatus: StepStatus = filterApplied
    ? "done"
    : coreDataReady
      ? "active"
      : "pending";
  const dataPointStatus: StepStatus = dataPointReady
    ? "done"
    : filterApplied
      ? "active"
      : "pending";

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <StepChip label="Loading recording" status={recordingStatus} />
      <Typography variant="caption" sx={{ color: "text.disabled" }} aria-hidden>
        ·
      </Typography>
      <StepChip label="Applying filter" status={filterStatus} />
      <Typography variant="caption" sx={{ color: "text.disabled" }} aria-hidden>
        ·
      </Typography>
      <StepChip label="Preparing data point" status={dataPointStatus} />
    </Stack>
  );
}

export interface AnalysisStatusbarProps {
  /** The open data acquisition — drives the filter-apply derivation. */
  inspectionId: string;
  /**
   * Re-trigger the failed readiness query (AC4). The host wires this to
   * refetch the step that failed; the overlay just exposes the affordance.
   */
  onRetry?: (step: ReadinessStep) => void;
}

/**
 * The single bottom-fixed analysis status overlay. Renders nothing (and so
 * occupies no space) when the workspace is idle.
 */
export function AnalysisStatusbar({
  inspectionId,
  onRetry,
}: AnalysisStatusbarProps): React.ReactElement | null {
  const phase = useAnalysisReadinessStore((s) => s.phase);
  const dataPointReady = useAnalysisReadinessStore((s) => s.dataPointReady);
  const failedStep = useAnalysisReadinessStore((s) => s.failedStep);

  const { applying } = useFilterApplyState(inspectionId);
  const progress = usePrefetchProgress();
  const controls = usePrefetchControls();
  const { cachedCount, totalCount, isDownloading, lastRun } = progress;

  // ANA-R2-05 — auto-hide the terminal "Downloaded …" state after a short
  // linger (PROG-03 auto-hide; AC3 = a terminal state, never silent
  // disappearance, but it must not linger forever). Acknowledging clears
  // `lastRun` on the scheduler → the overlay unmounts (if otherwise idle).
  useEffect(() => {
    if (!lastRun) return;
    const t = setTimeout(() => {
      prefetchScheduler.acknowledgeRun();
    }, TERMINAL_LINGER_MS);
    return () => {
      clearTimeout(t);
    };
  }, [lastRun]);

  // Visibility derivation (AC2 / the story's "visible iff" list):
  //   * readiness in error → the failed step + Retry (highest priority);
  //   * readiness not yet dataPointReady → the step sequence;
  //   * a filter is applying → "Applying filter …";
  //   * an explicit dataset download is running → the count + Cancel;
  //   * a download run just finished → the terminal "Downloaded …" state
  //     (ANA-R2-05 AC3), until it auto-hides.
  // Window-prefetch-only activity is INVISIBLE here: it never sets
  // `isDownloading` / `lastRun`, and once `dataPointReady` + not applying,
  // none of the arms fire → the overlay unmounts.
  const isError = phase === "error";
  const readinessActive = isError || !dataPointReady;
  const visible =
    readinessActive || applying || isDownloading || lastRun !== null;
  if (!visible) return null;

  let content: React.ReactElement;
  if (isError && failedStep) {
    content = (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 1 }}>
        <CircleAlertIcon
          sx={{ fontSize: 16, color: "error.main" }}
          aria-hidden
        />
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          Couldn&apos;t load {STEP_LABELS[failedStep]}.
        </Typography>
        <Button
          size="small"
          variant="text"
          onClick={() => {
            onRetry?.(failedStep);
          }}
          data-testid="statusbar-retry"
          sx={{ minWidth: 0, py: 0, px: 1 }}
        >
          Retry
        </Button>
      </Stack>
    );
  } else if (readinessActive) {
    // Still opening — the step sequence (banner content) wins over the
    // applying/download readouts (they are subsumed by "Applying filter").
    content = <ReadinessSteps />;
  } else if (isDownloading) {
    // Explicit dataset download — an active "Downloading images n/N" readout
    // (ANA-R2-05 AC1) + a Cancel passthrough. Takes precedence over a bare
    // "applying" because it carries the more specific signal (and Cancel
    // affordance).
    content = (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        data-testid="statusbar-cache-downloading"
      >
        <DownloadIcon
          sx={{ fontSize: 16, color: "text.secondary" }}
          aria-hidden
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          Downloading images {cachedCount}/{totalCount}
        </Typography>
        {controls ? (
          <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={<XIcon fontSize="small" />}
            onClick={controls.cancelDataset}
            data-testid="statusbar-cache-cancel"
            sx={{ minWidth: 0, py: 0, px: 1 }}
          >
            Cancel
          </Button>
        ) : null}
      </Stack>
    );
  } else if (lastRun) {
    // ANA-R2-05 AC3 — terminal state of the just-finished download run. Never
    // a silent disappearance: a success ("Downloaded N images") or a partial
    // ("Downloaded n of N — some failed"). Auto-hides after the linger above.
    const someFailed = lastRun.failed > 0;
    content = (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        data-testid="statusbar-cache-complete"
      >
        {someFailed ? (
          <CircleAlertIcon
            sx={{ fontSize: 16, color: "warning.main" }}
            aria-hidden
          />
        ) : (
          <CheckIcon sx={{ fontSize: 16, color: "success.main" }} aria-hidden />
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {someFailed
            ? `Downloaded ${String(lastRun.completed)} of ${String(
                lastRun.total,
              )} images — some failed`
            : `Downloaded ${String(lastRun.completed)} image${
                lastRun.completed === 1 ? "" : "s"
              }`}
        </Typography>
      </Stack>
    );
  } else {
    // applying — a filter change is in flight (data-points out-of-sync).
    content = (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        data-testid="statusbar-applying"
      >
        <CircularProgress size={12} thickness={5} aria-hidden />
        <Typography variant="caption" color="text.secondary">
          Applying filter …
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="analysis-statusbar"
      sx={{
        // Bottom-fixed overlay over the whole page (AC1 — zero layout shift;
        // AC2 — pinned to the viewport bottom on scroll). Content scrolls
        // behind it; the bar is never in flow.
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (t) => t.zIndex.appBar - 1,
        // Canvas-coloured (workspace-chrome rule), subtle top hairline + a
        // faint elevation so it reads as an overlay — no glass.
        bgcolor: "background.default",
        borderTop: 1,
        borderColor: "divider",
        boxShadow: (t) => `0 -1px 4px ${t.palette.action.hover}`,
        px: 2,
        py: 0.5,
        minHeight: 32,
        display: "flex",
        alignItems: "center",
      }}
    >
      {content}
    </Box>
  );
}
